import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ContractInsightHeading } from './ContractInsightHeading';
import { ContractKpiBoxes } from './ContractKpiBoxes';
import { contractOwnFactor, contractStaffing } from './contractStaffing';
import { ContractOwnWorkBudget } from './ContractStaffingCharts';
import { usePlanEditor } from './editor';

import { useBudgetModel } from '@/analytics';
import { SectionHeading } from '@/components/ui';

/**
 * The rail's last section in the project view (2026-09-29, the pilot's ask):
 * one block per contract — its name, its billing method with the letter badge
 * after it, and `ContractKpiBoxes`, the KPI boxes the task panel's Contract
 * insight shows for ONE contract — without its Planned box (2026-10-29, the
 * designer's ask), so three here. Shared, so the two read alike (since
 * 2026-10-29 the boxes, before that the rings and tiles). Nothing without a
 * contract.
 */
export const ContractInsightsSection = () => {
  const { contractInputs, consumption, project, state, resolveRate, resolveCost } = usePlanEditor();
  const budgetModel = useBudgetModel();
  const { t } = useTranslation(['pmPlanningPanel', 'pmCommon']);
  const metric = budgetModel.config.metric;

  if (contractInputs.length === 0) return null;

  return (
    <Box data-automation-id="contract-insights" sx={{ pt: 6 }}>
      <Box sx={{ mb: 2 }}>
        <SectionHeading icon="contract">{t('shared.ContractInsights')}</SectionHeading>
      </Box>
      {/*
        A divider between contracts, 24px either side (2026-10-01, the pilot's
        ask): half the section dividers' 48px, so the section still reads as one.
      */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          '& > [data-automation-id="contract-insight-row"] + [data-automation-id="contract-insight-row"]':
            {
              mt: 3,
              pt: 3,
              borderTop: 'var(--border-width-thin) solid var(--border-divider)',
            },
        }}
      >
        {contractInputs.map((contract) => {
          const staffing = contractStaffing(
            contract.id,
            state.document.plan,
            consumption,
            resolveRate,
            resolveCost,
          );
          if (!staffing) return null;
          const ownFactor = contractOwnFactor(contract, budgetModel.config);
          return (
            <Box
              key={contract.id}
              data-automation-id="contract-insight-row"
              data-contract-id={contract.id}
            >
              <ContractInsightHeading contract={contract} metric={metric} ownFactor={ownFactor} />
              {/* The Own Work budget, as a text line under the heading, left aligned. */}
              <Box
                data-automation-id="contract-insight-meta"
                sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}
              >
                <ContractOwnWorkBudget staffing={staffing} />
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <ContractKpiBoxes
                  staffing={staffing}
                  contractType={contract.type}
                  currency={project.document.currency}
                  ownFactor={ownFactor}
                  metric={metric}
                  showPlanned={false}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
